import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Button,
  Card,
  Row,
  Col,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  message,
  Slider,
  Checkbox,
  ColorPicker,
  Empty,
  Spin,
  Divider,
  Tag,
  Segmented
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  HolderOutlined,
  TableOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import payslipTemplateBuilderService from '../services/payslipTemplateBuilderService';
import settingsService from '../services/settingsService';
import {
  canCreatePayslipTemplate,
  canDeletePayslipTemplate,
  canEditPayslipTemplate,
  canViewPayslipTemplates
} from '../utils/permissions';
import './PayslipTemplateBuilder.css';

const DEFAULT_LAYOUT = {
  header: [],
  sections: [],
  footer: [],
  sectionSettings: {
    header: { width: 100, align: 'left', marginTop: 0, marginBottom: 12, marginLeft: 0, marginRight: 0 },
    sections: { width: 100, align: 'left', marginTop: 0, marginBottom: 12, marginLeft: 0, marginRight: 0 },
    footer: { width: 100, align: 'left', marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 }
  }
};

const ZONES = ['header', 'sections', 'footer'];

const sampleValues = {
  logo: 'LOGO',
  organizationName: 'Acme Technologies Pvt Ltd',
  organizationAddress: 'Bengaluru, Karnataka',
  payslipTitle: 'Pay Slip',
  payslipMonth: 'April 2026',
  employeeId: 'EMP-1024',
  employeeName: 'Aarav Sharma',
  designation: 'Senior Developer',
  department: 'Engineering',
  joinDate: '12 Jun 2022',
  panNumber: 'ABCDE1234F',
  uanNumber: '100123456789',
  esiNumber: 'ESI-54821',
  pfNumber: 'PF-908172',
  bankName: 'HDFC Bank',
  accountNumber: '**** 4381',
  ifscCode: 'HDFC0001234',
  grossWages: 'Rs. 92,000',
  netSalary: 'Rs. 78,450',
  amountInWords: 'Rupees seventy eight thousand four hundred fifty only',
  workingDays: '30',
  paidDays: '29',
  leavesDays: '1',
  organizationSignature: 'Authorized Signature',
  authorizedBy: 'HR Manager',
  generatedDate: '06 May 2026'
};

const createId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createDefaultStyle = (style = {}) => ({
  bold: false,
  fontSize: 12,
  align: 'left',
  color: '#111827',
  backgroundColor: '',
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  logoWidth: 120,
  logoHeight: 120,
  tableBorder: true,
  ...style
});

const createTemplateItem = (field) => {
  const fieldId = field.fieldId || field.id;
  const isTable = field.type === 'table';

  return {
    ...field,
    fieldId,
    templateItemId: createId(fieldId),
    columnCount: isTable ? 2 : 1,
    columnWidths: isTable ? [50, 50] : [100],
    tableRows: isTable ? 3 : undefined,
    tableColumns: isTable ? 2 : undefined,
    tableColumnWidths: isTable ? [50, 50] : undefined,
    tableCells: isTable
      ? Array.from({ length: 3 }, () => Array.from({ length: 2 }, () => null))
      : undefined,
    style: createDefaultStyle()
  };
};

const createColumnContainer = (columnCount, zone = 'header') => ({
  id: `columnLayout${columnCount}`,
  fieldId: `columnLayout${columnCount}`,
  templateItemId: createId(`column-layout-${columnCount}`),
  label: `${zone.charAt(0).toUpperCase() + zone.slice(1)} ${columnCount} Columns`,
  type: 'container',
  category: zone,
  columnCount,
  columnWidths: Array.from({ length: columnCount }, () => Math.floor(100 / columnCount)),
  columns: Array.from({ length: columnCount }, () => []),
  style: createDefaultStyle()
});

const createTableFormat = (zone = 'sections') => createTemplateItem({
  id: `customTable${zone}`,
  fieldId: `customTable${zone}`,
  label: `${zone.charAt(0).toUpperCase() + zone.slice(1)} Table`,
  type: 'table',
  category: zone
});

const normalizeTableCell = (cell, rowIndex, columnIndex, rowCount, columnCount) => {
  if (!cell) return null;

  const normalized = normalizeTemplateItem(cell);
  return {
    ...normalized,
    rowSpan: Math.min(Math.max(normalized.rowSpan || 1, 1), rowCount - rowIndex),
    colSpan: Math.min(Math.max(normalized.colSpan || 1, 1), columnCount - columnIndex)
  };
};

const resizeRows = (cells, rowCount, columnCount) => (
  Array.from({ length: rowCount }, (_, rowIndex) => (
    Array.from({ length: columnCount }, (_, columnIndex) => cells?.[rowIndex]?.[columnIndex] || null)
  ))
);

const isCellCoveredBySpan = (table, targetRow, targetColumn) => {
  for (let rowIndex = 0; rowIndex <= targetRow; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < (table.tableColumns || 0); columnIndex += 1) {
      if (rowIndex === targetRow && columnIndex === targetColumn) return false;

      const cell = table.tableCells?.[rowIndex]?.[columnIndex];
      const rowSpan = cell?.rowSpan || 1;
      const colSpan = cell?.colSpan || 1;

      if (
        cell &&
        rowIndex <= targetRow &&
        rowIndex + rowSpan > targetRow &&
        columnIndex <= targetColumn &&
        columnIndex + colSpan > targetColumn
      ) {
        return true;
      }
    }
  }

  return false;
};

const normalizeTemplateItem = (field) => {
  if (!field) return field;

  const fieldId = field.fieldId || field.id;
  const isTable = field.type === 'table';
  const isContainer = field.type === 'container';
  const columnCount = field.columnCount || field.tableColumns || (isTable ? 2 : 1);
  const currentWidths = Array.isArray(field.columnWidths) ? field.columnWidths : [];
  const defaultWidth = Math.floor(100 / columnCount);
  const base = {
    ...field,
    fieldId,
    id: field.id || fieldId,
    templateItemId: field.templateItemId || createId(fieldId),
    columnCount,
    columnWidths: Array.from({ length: columnCount }, (_, index) => currentWidths[index] || defaultWidth),
    style: createDefaultStyle(field.style)
  };

  if (isContainer) {
    return {
      ...base,
      columns: Array.from({ length: columnCount }, (_, columnIndex) => (
        (field.columns?.[columnIndex] || []).map(normalizeTemplateItem)
      ))
    };
  }

  if (isTable) {
    const tableColumns = field.tableColumns || columnCount || 2;
    const tableRows = field.tableRows || 3;
    const tableWidths = Array.isArray(field.tableColumnWidths) ? field.tableColumnWidths : base.columnWidths;

    return {
      ...base,
      tableRows,
      tableColumns,
      tableColumnWidths: Array.from(
        { length: tableColumns },
        (_, index) => tableWidths[index] || Math.floor(100 / tableColumns)
      ),
      tableCells: resizeRows(field.tableCells, tableRows, tableColumns).map((row, rowIndex) => (
        row.map((cell, columnIndex) => normalizeTableCell(cell, rowIndex, columnIndex, tableRows, tableColumns))
      ))
    };
  }

  return base;
};

const normalizeLayout = (layout) => {
  const source = layout || DEFAULT_LAYOUT;
  const normalized = ZONES.reduce((acc, zone) => {
    acc[zone] = Array.isArray(source[zone]) ? source[zone].map(normalizeTemplateItem) : [];
    return acc;
  }, {});

  normalized.sectionSettings = ZONES.reduce((acc, zone) => {
    acc[zone] = {
      ...DEFAULT_LAYOUT.sectionSettings[zone],
      ...(source.sectionSettings?.[zone] || {})
    };
    return acc;
  }, {});

  return normalized;
};

function PayslipTemplateBuilder() {
  const { user } = useSelector((state) => state.auth);
  const canCreate = canCreatePayslipTemplate(user);
  const canEdit = canEditPayslipTemplate(user);
  const canView = canViewPayslipTemplates(user);
  const canDelete = canDeletePayslipTemplate(user);
  const [templates, setTemplates] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form] = Form.useForm();
  const [draggedElement, setDraggedElement] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedSection, setSelectedSection] = useState('header');
  const [headerTemplateColumns, setHeaderTemplateColumns] = useState(2);
  const [organizationSettings, setOrganizationSettings] = useState(null);
  const [templateLayout, setTemplateLayout] = useState(DEFAULT_LAYOUT);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await payslipTemplateBuilderService.getAll(1, 100, {
        organizationId: user?.organizationId
      });
      setTemplates(response.data || []);
    } catch (error) {
      message.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId]);

  const loadAvailableFields = useCallback(async () => {
    try {
      const response = await payslipTemplateBuilderService.getAvailableFields();
      setAvailableFields(response.data || []);
    } catch (error) {
      message.error('Failed to load available fields');
    }
  }, []);

  const loadOrganizationSettings = useCallback(async () => {
    if (!user?.organizationId) return;

    try {
      const response = await settingsService.getOrganizationSettings(user.organizationId);
      setOrganizationSettings(response.data || null);
    } catch (error) {
      setOrganizationSettings(null);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    loadTemplates();
    loadAvailableFields();
    loadOrganizationSettings();
  }, [loadTemplates, loadAvailableFields, loadOrganizationSettings]);

  const handleCreateTemplate = () => {
    setIsEditMode(false);
    setIsPreviewMode(false);
    setSelectedTemplate(null);
    setSelectedPath(null);
    setSelectedSection('header');
    setTemplateLayout(DEFAULT_LAYOUT);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template) => {
    setIsEditMode(true);
    setIsPreviewMode(false);
    setSelectedTemplate(template);
    setSelectedPath(null);
    setSelectedSection('header');
    setTemplateLayout(normalizeLayout(template.layoutConfig));
    form.setFieldsValue({
      name: template.name,
      description: template.description
    });
    setIsModalOpen(true);
  };

  const handleDeleteTemplate = (templateId) => {
    Modal.confirm({
      title: 'Delete Template',
      content: 'Are you sure you want to delete this template?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await payslipTemplateBuilderService.delete(templateId);
          message.success('Template deleted successfully');
          loadTemplates();
        } catch (error) {
          message.error('Failed to delete template');
        }
      }
    });
  };

  const handleSaveTemplate = async (values) => {
    try {
      const payload = {
        ...values,
        layoutConfig: normalizeLayout(templateLayout)
      };

      if (isEditMode && selectedTemplate?.id) {
        await payslipTemplateBuilderService.update(selectedTemplate.id, payload);
        message.success('Template updated successfully');
      } else {
        await payslipTemplateBuilderService.create(payload);
        message.success('Template created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      loadTemplates();
    } catch (error) {
      message.error('Failed to save template');
    }
  };

  const handleDragStart = (payload) => {
    setDraggedElement(payload);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeDraggedItem = (layout, payload) => {
    let item = null;

    if (payload.source === 'layout') {
      item = layout[payload.zone]?.[payload.index];
      layout[payload.zone] = layout[payload.zone].filter((_, index) => index !== payload.index);
    }

    if (payload.source === 'container') {
      const container = layout[payload.zone]?.[payload.containerIndex];
      item = container?.columns?.[payload.columnIndex]?.[payload.fieldIndex];
      if (container) {
        container.columns[payload.columnIndex] = container.columns[payload.columnIndex]
          .filter((_, index) => index !== payload.fieldIndex);
      }
    }

    return item;
  };

  const getDraggedFieldItem = (layout) => {
    if (!draggedElement) return null;

    if (draggedElement.source === 'palette') {
      return createTemplateItem(draggedElement.field);
    }

    return removeDraggedItem(layout, draggedElement);
  };

  const handleDropInZone = (zone, targetIndex = null) => {
    if (!draggedElement) return;

    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      const itemToInsert = getDraggedFieldItem(next);

      if (!itemToInsert) return prev;

      const insertIndex = targetIndex === null ? next[zone].length : targetIndex;
      next[zone].splice(insertIndex, 0, itemToInsert);
      return next;
    });

    setDraggedElement(null);
  };

  const handleDropInContainerColumn = (zone, containerIndex, columnIndex) => {
    if (!draggedElement) return;

    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      const container = next[zone]?.[containerIndex];
      const itemToInsert = getDraggedFieldItem(next);

      if (!container || !itemToInsert || itemToInsert.type === 'container') return prev;

      container.columns[columnIndex] = [...(container.columns[columnIndex] || []), itemToInsert];
      return next;
    });

    setDraggedElement(null);
  };

  const handleDropInTableCell = (zone, tableIndex, rowIndex, columnIndex) => {
    if (!draggedElement) return;

    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      const table = next[zone]?.[tableIndex];
      const itemToInsert = getDraggedFieldItem(next);

      if (
        !table ||
        !itemToInsert ||
        itemToInsert.type === 'table' ||
        itemToInsert.type === 'container' ||
        isCellCoveredBySpan(table, rowIndex, columnIndex)
      ) {
        return prev;
      }

      table.tableCells[rowIndex][columnIndex] = itemToInsert;
      return next;
    });

    setDraggedElement(null);
  };

  const handleRemoveField = (zone, index) => {
    setTemplateLayout((prev) => ({
      ...prev,
      [zone]: prev[zone].filter((_, i) => i !== index)
    }));
    setSelectedPath(null);
  };

  const handleRemoveNestedField = (zone, containerIndex, columnIndex, fieldIndex) => {
    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      const container = next[zone]?.[containerIndex];
      if (!container) return prev;

      container.columns[columnIndex] = container.columns[columnIndex].filter((_, index) => index !== fieldIndex);
      return next;
    });
    setSelectedPath(null);
  };

  const handleClearTableCell = (zone, tableIndex, rowIndex, columnIndex) => {
    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      const table = next[zone]?.[tableIndex];
      if (!table) return prev;

      table.tableCells[rowIndex][columnIndex] = null;
      return next;
    });
    setSelectedPath(null);
  };

  const addHeaderTemplate = () => {
    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      next.header = [...next.header, createColumnContainer(headerTemplateColumns, 'header')];
      return next;
    });
  };

  const addTableToZone = (zone) => {
    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      next[zone] = [...next[zone], createTableFormat(zone)];
      return next;
    });
  };

  const getColorValue = (value, fallback = '') => value || fallback;

  const handleStyleColorChange = (key, color, fallback = '') => {
    const nextColor = color?.toHexString ? color.toHexString() : color;
    updateSelectedField((field) => ({
      ...field,
      style: { ...field.style, [key]: nextColor || fallback }
    }));
  };

  const getFieldBoxStyle = (field) => ({
    marginTop: field.style?.marginTop || 0,
    marginBottom: field.style?.marginBottom || 0,
    marginLeft: field.style?.marginLeft || 0,
    marginRight: field.style?.marginRight || 0,
    backgroundColor: field.style?.backgroundColor || undefined
  });

  const handleTableCellSpanChange = (key, value) => {
    if (selectedPath?.kind !== 'tableCell') return;

    updateSelectedField((field) => ({
      ...field,
      [key]: value || 1
    }));
  };

  const getSelectedField = () => {
    if (!selectedPath) return null;

    const zoneItems = templateLayout[selectedPath.zone] || [];

    if (selectedPath.kind === 'item') {
      return zoneItems[selectedPath.index] || null;
    }

    if (selectedPath.kind === 'containerField') {
      return zoneItems[selectedPath.containerIndex]
        ?.columns?.[selectedPath.columnIndex]?.[selectedPath.fieldIndex] || null;
    }

    if (selectedPath.kind === 'tableCell') {
      return zoneItems[selectedPath.tableIndex]
        ?.tableCells?.[selectedPath.rowIndex]?.[selectedPath.columnIndex] || null;
    }

    return null;
  };

  const getSelectedTable = () => {
    if (selectedPath?.kind !== 'tableCell') return null;
    return templateLayout[selectedPath.zone]?.[selectedPath.tableIndex] || null;
  };

  const updateSelectedField = (updater) => {
    if (!selectedPath) return;

    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      let current = null;

      if (selectedPath.kind === 'item') {
        current = next[selectedPath.zone]?.[selectedPath.index];
        if (!current) return prev;
        next[selectedPath.zone][selectedPath.index] =
          typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      }

      if (selectedPath.kind === 'containerField') {
        current = next[selectedPath.zone]?.[selectedPath.containerIndex]
          ?.columns?.[selectedPath.columnIndex]?.[selectedPath.fieldIndex];
        if (!current) return prev;
        next[selectedPath.zone][selectedPath.containerIndex].columns[selectedPath.columnIndex][selectedPath.fieldIndex] =
          typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      }

      if (selectedPath.kind === 'tableCell') {
        current = next[selectedPath.zone]?.[selectedPath.tableIndex]
          ?.tableCells?.[selectedPath.rowIndex]?.[selectedPath.columnIndex];
        if (!current) return prev;
        next[selectedPath.zone][selectedPath.tableIndex].tableCells[selectedPath.rowIndex][selectedPath.columnIndex] =
          typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      }

      return next;
    });
  };

  const handleColumnWidthChange = (columnIndex, width) => {
    updateSelectedField((field) => ({
      ...field,
      columnWidths: field.columnWidths.map((columnWidth, index) => (
        index === columnIndex ? width : columnWidth
      ))
    }));
  };

  const handleTableSizeChange = (key, value) => {
    updateSelectedField((field) => {
      const tableRows = key === 'tableRows' ? value : field.tableRows;
      const tableColumns = key === 'tableColumns' ? value : field.tableColumns;

      const tableCells = resizeRows(field.tableCells, tableRows, tableColumns).map((row, rowIndex) => (
        row.map((cell, columnIndex) => normalizeTableCell(cell, rowIndex, columnIndex, tableRows, tableColumns))
      ));

      return {
        ...field,
        tableRows,
        tableColumns,
        tableColumnWidths: Array.from(
          { length: tableColumns },
          (_, index) => field.tableColumnWidths?.[index] || Math.floor(100 / tableColumns)
        ),
        tableCells
      };
    });
  };

  const handleTableColumnWidthChange = (columnIndex, width) => {
    updateSelectedField((field) => ({
      ...field,
      tableColumnWidths: field.tableColumnWidths.map((columnWidth, index) => (
        index === columnIndex ? width : columnWidth
      ))
    }));
  };

  const handleTableRowChange = (direction) => {
    updateSelectedField((field) => {
      const currentRows = field.tableRows || 1;
      const tableRows = direction === 'add'
        ? Math.min(currentRows + 1, 12)
        : Math.max(currentRows - 1, 1);
      const tableColumns = field.tableColumns || 1;
      const tableCells = resizeRows(field.tableCells, tableRows, tableColumns).map((row, rowIndex) => (
        row.map((cell, columnIndex) => normalizeTableCell(cell, rowIndex, columnIndex, tableRows, tableColumns))
      ));

      return {
        ...field,
        tableRows,
        tableCells
      };
    });
  };

  const handleTableColumnChange = (direction) => {
    updateSelectedField((field) => {
      const currentColumns = field.tableColumns || 1;
      const tableColumns = direction === 'add'
        ? Math.min(currentColumns + 1, 8)
        : Math.max(currentColumns - 1, 1);
      const tableRows = field.tableRows || 1;
      const tableCells = resizeRows(field.tableCells, tableRows, tableColumns).map((row, rowIndex) => (
        row.map((cell, columnIndex) => normalizeTableCell(cell, rowIndex, columnIndex, tableRows, tableColumns))
      ));
      const tableColumnWidths = Array.from(
        { length: tableColumns },
        (_, index) => field.tableColumnWidths?.[index] || Math.floor(100 / tableColumns)
      );

      return {
        ...field,
        tableColumns,
        tableColumnWidths,
        tableCells
      };
    });
  };

  const handleSectionSettingChange = (zone, key, value) => {
    setTemplateLayout((prev) => {
      const next = normalizeLayout(prev);
      next.sectionSettings[zone] = {
        ...next.sectionSettings[zone],
        [key]: value
      };
      return next;
    });
  };

  const getSectionStyle = (zone) => {
    const settings = templateLayout.sectionSettings?.[zone] || DEFAULT_LAYOUT.sectionSettings[zone];
    const align = settings.align || 'left';
    const style = {
      width: `${settings.width || 100}%`,
      marginTop: settings.marginTop || 0,
      marginBottom: settings.marginBottom || 0
    };

    if (align === 'center') {
      return {
        ...style,
        marginLeft: 'auto',
        marginRight: 'auto'
      };
    }

    if (align === 'right') {
      return {
        ...style,
        marginLeft: 'auto',
        marginRight: settings.marginRight || 0
      };
    }

    return {
      ...style,
      marginLeft: settings.marginLeft || 0,
      marginRight: 'auto'
    };
  };

  const selectedField = getSelectedField();
  const selectedTable = getSelectedTable();

  const renderFieldChip = (field, actions = null) => (
    <Space size={6} className="field-chip">
      <HolderOutlined className="drag-handle" />
      <span>{field.label}</span>
      <Tag color="green" className="field-type-tag">{field.type}</Tag>
      {actions}
    </Space>
  );

  const renderTableDesigner = (field, zone, index) => (
    <div
      className={`designer-table-wrap ${
        selectedPath?.kind === 'item' && selectedPath?.zone === zone && selectedPath?.index === index ? 'selected' : ''
      }`}
      style={getFieldBoxStyle(field)}
      onClick={(event) => {
        event.stopPropagation();
        setSelectedPath({ kind: 'item', zone, index });
      }}
    >
      <div className="designer-table-title">
        <TableOutlined />
        <span>{field.label}</span>
      </div>
        <table className={`designer-table ${field.style?.tableBorder === false ? 'no-border' : ''}`}>
        <colgroup>
          {field.tableColumnWidths.map((width, columnIndex) => (
            <col key={columnIndex} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <tbody>
          {field.tableCells.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, columnIndex) => {
                if (isCellCoveredBySpan(field, rowIndex, columnIndex)) return null;

                return (
                  <td
                    key={columnIndex}
                    colSpan={cell?.colSpan || 1}
                    rowSpan={cell?.rowSpan || 1}
                    style={{ backgroundColor: cell?.style?.backgroundColor || undefined }}
                    onDragOver={handleDragOver}
                    onDrop={(event) => {
                      event.stopPropagation();
                      handleDropInTableCell(zone, index, rowIndex, columnIndex);
                    }}
                  >
                    {cell ? (
                      <div
                        className={`table-cell-field ${
                          selectedPath?.kind === 'tableCell' &&
                          selectedPath?.zone === zone &&
                          selectedPath?.tableIndex === index &&
                          selectedPath?.rowIndex === rowIndex &&
                          selectedPath?.columnIndex === columnIndex ? 'selected' : ''
                        }`}
                        style={getFieldBoxStyle(cell)}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPath({ kind: 'tableCell', zone, tableIndex: index, rowIndex, columnIndex });
                        }}
                      >
                        <span>{cell.label}</span>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClearTableCell(zone, index, rowIndex, columnIndex);
                          }}
                        />
                      </div>
                    ) : (
                      <span className="drop-placeholder">Drop key</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderContainerDesigner = (field, zone, index) => (
    <div
      className={`column-container ${
        selectedPath?.kind === 'item' && selectedPath?.zone === zone && selectedPath?.index === index ? 'selected' : ''
      }`}
      style={getFieldBoxStyle(field)}
      onClick={(event) => {
        event.stopPropagation();
        setSelectedPath({ kind: 'item', zone, index });
      }}
    >
      <div className="column-container-title">
        <AppstoreOutlined />
        <span>{field.label}</span>
      </div>
      <div
        className="column-container-grid"
        style={{
          gridTemplateColumns: field.columnWidths.map((width) => `${width}fr`).join(' ')
        }}
      >
        {field.columns.map((columnFields, columnIndex) => (
          <div
            key={columnIndex}
            className="column-drop-zone"
            onDragOver={handleDragOver}
            onDrop={(event) => {
              event.stopPropagation();
              handleDropInContainerColumn(zone, index, columnIndex);
            }}
          >
            <div className="column-label">Column {columnIndex + 1}</div>
            {columnFields.length === 0 ? (
              <span className="drop-placeholder">Drop key</span>
            ) : (
              columnFields.map((nestedField, fieldIndex) => (
                <div
                  key={nestedField.templateItemId || fieldIndex}
                  className={`nested-field ${
                    selectedPath?.kind === 'containerField' &&
                    selectedPath?.zone === zone &&
                    selectedPath?.containerIndex === index &&
                    selectedPath?.columnIndex === columnIndex &&
                    selectedPath?.fieldIndex === fieldIndex ? 'selected' : ''
                  }`}
                  style={getFieldBoxStyle(nestedField)}
                  draggable
                  onDragStart={() => handleDragStart({
                    source: 'container',
                    zone,
                    containerIndex: index,
                    columnIndex,
                    fieldIndex
                  })}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedPath({ kind: 'containerField', zone, containerIndex: index, columnIndex, fieldIndex });
                  }}
                >
                  {renderFieldChip(
                    nestedField,
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemoveNestedField(zone, index, columnIndex, fieldIndex);
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderZoneField = (field, zone, index) => {
    if (field.type === 'container') {
      return renderContainerDesigner(field, zone, index);
    }

    if (field.type === 'table') {
      return renderTableDesigner(field, zone, index);
    }

    return (
      <div
        className={`zone-field ${
          selectedPath?.kind === 'item' && selectedPath?.zone === zone && selectedPath?.index === index ? 'selected' : ''
        }`}
        style={getFieldBoxStyle(field)}
        draggable
        onClick={() => setSelectedPath({ kind: 'item', zone, index })}
        onDragStart={() => handleDragStart({ source: 'layout', zone, index })}
        onDragOver={handleDragOver}
        onDrop={(event) => {
          event.stopPropagation();
          handleDropInZone(zone, index);
        }}
      >
        {renderFieldChip(field)}
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            handleRemoveField(zone, index);
          }}
        />
      </div>
    );
  };

  const renderPreviewValue = (field) => {
    const fieldKey = field.fieldId || field.id;
    const style = {
      fontWeight: field.style?.bold ? 700 : 400,
      fontSize: `${field.style?.fontSize || 12}px`,
      textAlign: field.style?.align || 'left',
      color: field.style?.color || undefined,
      ...getFieldBoxStyle(field)
    };

    if (field.type === 'image' || fieldKey === 'logo') {
      return (
        <div
          className={`preview-logo ${organizationSettings?.logo ? 'has-image' : ''}`}
          style={{
            width: field.style?.logoWidth || 120,
            height: field.style?.logoHeight || 54,
            fontSize: `${field.style?.fontSize || 12}px`,
            fontWeight: field.style?.bold ? 700 : 600,
            marginTop: field.style?.marginTop || 0,
            marginBottom: field.style?.marginBottom || 0,
            marginLeft: field.style?.marginLeft || 0,
            marginRight: field.style?.marginRight || 0,
            backgroundColor: field.style?.backgroundColor || undefined
          }}
        >
          {organizationSettings?.logo ? (
            <img src={organizationSettings.logo} alt="Organization logo" />
          ) : (
            'SAMPLE LOGO'
          )}
        </div>
      );
    }

    if (field.type === 'customText') {
      return (
        <div className="preview-custom-text" style={{ ...style, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          {field.customValue || field.defaultValue || 'Enter your text here'}
        </div>
      );
    }

    return (
      <div className="preview-value" style={style}>
        <span style={{ color: field.style?.color || undefined }}>{field.label}</span>
        <strong style={{ color: field.style?.color || undefined }}>
          {sampleValues[fieldKey] || field.defaultValue || 'Sample value'}
        </strong>
      </div>
    );
  };

  const renderPreviewItem = (field) => {
    if (field.type === 'container') {
      return (
        <div
          className="preview-column-container"
          style={{
            gridTemplateColumns: field.columnWidths.map((width) => `${width}fr`).join(' '),
            ...getFieldBoxStyle(field)
          }}
        >
          {field.columns.map((columnFields, columnIndex) => (
            <div key={columnIndex} className="preview-column">
              {columnFields.map((nestedField) => (
                <div key={nestedField.templateItemId} className="preview-field">
                  {renderPreviewValue(nestedField)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    if (field.type === 'table') {
      return (
        <table
          className={`preview-table ${field.style?.tableBorder === false ? 'no-border' : ''}`}
          style={{
            fontWeight: field.style?.bold ? 700 : 400,
            fontSize: `${field.style?.fontSize || 12}px`,
            color: field.style?.color || undefined,
            ...getFieldBoxStyle(field)
          }}
        >
          <colgroup>
            {field.tableColumnWidths.map((width, index) => (
              <col key={index} style={{ width: `${width}%` }} />
            ))}
          </colgroup>
          <tbody>
            {field.tableCells.map((row, rowIndex) => (
              <tr key={rowIndex}>
              {row.map((cell, columnIndex) => {
                if (isCellCoveredBySpan(field, rowIndex, columnIndex)) return null;

                return (
                  <td
                    key={columnIndex}
                    colSpan={cell?.colSpan || 1}
                    rowSpan={cell?.rowSpan || 1}
                    style={{ backgroundColor: cell?.style?.backgroundColor || undefined }}
                  >
                    {cell ? renderPreviewValue(cell) : <span className="preview-muted">Empty</span>}
                  </td>
                );
              })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return renderPreviewValue(field);
  };

  const renderTemplatePreview = () => (
    <div className="template-preview-sheet">
      {ZONES.map((zone) => (
        <div
          key={zone}
          className={`preview-zone preview-zone-${zone}`}
          style={getSectionStyle(zone)}
        >
          {templateLayout[zone]?.length > 0 ? (
            templateLayout[zone].map((field) => (
              <div key={field.templateItemId} className={`preview-field preview-field-${field.type}`}>
                {renderPreviewItem(field)}
              </div>
            ))
          ) : (
            <div className="preview-empty">{zone}</div>
          )}
        </div>
      ))}
    </div>
  );

  const fieldsByCategory = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {});

  if (!isModalOpen) {
    return (
      <Layout className="payslip-template-builder" style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>Payslip Template Builder</h1>
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateTemplate}
            >
              Create Template
            </Button>
          )}
        </div>

        <Spin spinning={loading}>
          {templates.length === 0 ? (
            <Empty description="No templates created yet" />
          ) : (
            <Row gutter={[16, 16]}>
              {templates.map((template) => (
                <Col key={template.id} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    cover={
                      <div className="template-card-cover">
                        <div>
                          <p><strong>Template:</strong> {template.name}</p>
                          <p><strong>Fields:</strong> {
                            ((template.layoutConfig?.header?.length || 0) +
                              (template.layoutConfig?.sections?.length || 0) +
                              (template.layoutConfig?.footer?.length || 0))
                          }</p>
                        </div>
                      </div>
                    }
                    actions={[
                      canView && (
                        <Button
                          type="text"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            setIsEditMode(false);
                            setIsPreviewMode(true);
                            setSelectedTemplate(template);
                            setSelectedPath(null);
                            setSelectedSection('header');
                            setTemplateLayout(normalizeLayout(template.layoutConfig));
                            form.setFieldsValue({
                              name: template.name,
                              description: template.description
                            });
                            setIsModalOpen(true);
                          }}
                          title="View"
                        >
                          
                        </Button>
                      ),
                      canEdit && (
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => handleEditTemplate(template)}
                          title="Edit"
                        >
                          
                        </Button>
                      ),
                      canDelete && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteTemplate(template.id)}
                          title="Delete"
                        >
                          
                        </Button>
                      )
                    ].filter(Boolean)}
                  >
                    <p><strong>{template.name}</strong></p>
                    <p style={{ fontSize: '12px', color: '#999' }}>{template.description}</p>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Spin>
      </Layout>
    );
  }

  return (
    <Modal
      className="payslip-template-builder-modal"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>{isEditMode ? 'Edit Template' : 'Create Template'}</span>
          {!isEditMode && (
            <Button
              type={isPreviewMode ? 'primary' : 'default'}
              icon={<EyeOutlined />}
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              title={isPreviewMode ? 'Exit Preview' : 'Preview Template'}
            >
              {isPreviewMode ? 'Exit Preview' : 'Preview'}
            </Button>
          )}
        </div>
      }
      open={isModalOpen}
      onCancel={() => {
        setIsModalOpen(false);
        setIsPreviewMode(false);
      }}
      width="94vw"
      bodyStyle={{ height: '82vh', overflow: 'auto' }}
      footer={isPreviewMode ? [
        <Button key="cancel" onClick={() => setIsModalOpen(false)}>Close</Button>
      ] : [
        <Button key="cancel" onClick={() => setIsModalOpen(false)}>Cancel</Button>,
        <Button key="submit" type="primary" icon={<SaveOutlined />} onClick={() => form.submit()}>
          Save Template
        </Button>
      ]}
    >
      <Form className="payslip-template-builder" form={form} layout="vertical" onFinish={handleSaveTemplate}>
        {isPreviewMode ? (
          <div style={{ padding: '20px' }}>
            <h2 style={{ marginBottom: '20px' }}>
              <strong>{selectedTemplate?.name}</strong>
            </h2>
            {selectedTemplate?.description && (
              <p style={{ color: '#666', marginBottom: '20px' }}>{selectedTemplate.description}</p>
            )}
            <Divider />
            {renderTemplatePreview()}
          </div>
        ) : (
          <>
            <Form.Item
              label="Template Name"
              name="name"
              rules={[{ required: true, message: 'Please enter template name' }]}
            >
              <Input placeholder="e.g., Standard Payslip Template 2024" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} placeholder="Template description" />
            </Form.Item>

            <Divider>Template Builder - Drag and Drop Elements</Divider>

            <Row gutter={16} className="builder-workspace">
          <Col xs={24} md={6}>
            <Card title="Available Fields" size="small" className="available-fields">
              <div className="header-template-tool">
                <p className="option-label">Header Template</p>
                <Space.Compact style={{ width: '100%' }}>
                  <Segmented
                    value={headerTemplateColumns}
                    onChange={setHeaderTemplateColumns}
                    options={[
                      { label: '2 Col', value: 2 },
                      { label: '3 Col', value: 3 }
                    ]}
                  />
                  <Button type="primary" icon={<PlusOutlined />} onClick={addHeaderTemplate}>
                    Add
                  </Button>
                </Space.Compact>
              </div>

              <Spin spinning={loading}>
                {Object.entries(fieldsByCategory).map(([category, fields]) => (
                  <div key={category} style={{ marginBottom: '12px' }}>
                    <p className="category-title">
                      {category.toUpperCase()}
                    </p>
                    {fields.map((field) => (
                      <div
                        key={field.id}
                        className="field-item"
                        draggable
                        onDragStart={() => handleDragStart({ source: 'palette', field })}
                      >
                        <span>{field.label}</span>
                        <Tag color="blue" className="field-type-tag">
                          {field.type}
                        </Tag>
                      </div>
                    ))}
                  </div>
                ))}
              </Spin>
            </Card>
          </Col>

          <Col xs={24} md={12} className="designer-scroll-column">
            {ZONES.map((zone) => (
              <Card
                key={zone}
                title={`${zone.charAt(0).toUpperCase() + zone.slice(1)}`}
                style={{ marginBottom: '16px' }}
                size="small"
                extra={(
                  <Button
                    size="small"
                    icon={<TableOutlined />}
                    onClick={() => addTableToZone(zone)}
                  >
                    Add table
                  </Button>
                )}
              >
                <div
                  className="template-zone"
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropInZone(zone)}
                >
                  {templateLayout[zone]?.length === 0 ? (
                    <Empty
                      description="Drag fields here"
                      style={{ margin: '20px 0' }}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ) : (
                    templateLayout[zone]?.map((field, index) => (
                      <div key={field.templateItemId || index} className="designer-item">
                        {renderZoneField(field, zone, index)}
                        {(field.type === 'container' || field.type === 'table') && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            className="designer-item-delete"
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveField(zone, index)}
                          />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card>
            ))}
          </Col>

          <Col xs={24} md={6}>
            <Card title="Field Options" size="small" className="field-options">
              {selectedField ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div>
                    <p className="option-label">Selected</p>
                    <Tag color="geekblue">{selectedField.label}</Tag>
                  </div>

                  <Checkbox
                    checked={!!selectedField.style?.bold}
                    onChange={(event) => updateSelectedField((field) => ({
                      ...field,
                      style: { ...field.style, bold: event.target.checked }
                    }))}
                  >
                    Bold
                  </Checkbox>

                  <div>
                    <p className="option-label">Font size</p>
                    <InputNumber
                      min={8}
                      max={48}
                      value={selectedField.style?.fontSize || 12}
                      onChange={(value) => updateSelectedField((field) => ({
                        ...field,
                        style: { ...field.style, fontSize: value || 12 }
                      }))}
                      addonAfter="px"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <p className="option-label">Text align</p>
                    <Segmented
                      block
                      value={selectedField.style?.align || 'left'}
                      onChange={(value) => updateSelectedField((field) => ({
                        ...field,
                        style: { ...field.style, align: value }
                      }))}
                      options={[
                        { label: 'Left', value: 'left' },
                        { label: 'Center', value: 'center' },
                        { label: 'Right', value: 'right' }
                      ]}
                    />
                  </div>

                  <Row gutter={8}>
                    <Col span={12}>
                      <p className="option-label">Text color</p>
                      <ColorPicker
                        value={getColorValue(selectedField.style?.color, '#111827')}
                        onChange={(color) => handleStyleColorChange('color', color, '#111827')}
                        showText
                      />
                    </Col>
                    <Col span={12}>
                      <p className="option-label">Fill color</p>
                      <Space.Compact>
                        <ColorPicker
                          value={getColorValue(selectedField.style?.backgroundColor, '#ffffff')}
                          onChange={(color) => handleStyleColorChange('backgroundColor', color)}
                          showText
                        />
                        <Button onClick={() => handleStyleColorChange('backgroundColor', '')}>
                          Clear
                        </Button>
                      </Space.Compact>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col span={12}>
                      <p className="option-label">Left margin</p>
                      <InputNumber
                        min={0}
                        max={120}
                        value={selectedField.style?.marginLeft || 0}
                        onChange={(value) => updateSelectedField((field) => ({
                          ...field,
                          style: { ...field.style, marginLeft: value || 0 }
                        }))}
                        addonAfter="px"
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col span={12}>
                      <p className="option-label">Right margin</p>
                      <InputNumber
                        min={0}
                        max={120}
                        value={selectedField.style?.marginRight || 0}
                        onChange={(value) => updateSelectedField((field) => ({
                          ...field,
                          style: { ...field.style, marginRight: value || 0 }
                        }))}
                        addonAfter="px"
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col span={12}>
                      <p className="option-label">Top margin</p>
                      <InputNumber
                        min={0}
                        max={120}
                        value={selectedField.style?.marginTop || 0}
                        onChange={(value) => updateSelectedField((field) => ({
                          ...field,
                          style: { ...field.style, marginTop: value || 0 }
                        }))}
                        addonAfter="px"
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col span={12}>
                      <p className="option-label">Bottom margin</p>
                      <InputNumber
                        min={0}
                        max={120}
                        value={selectedField.style?.marginBottom || 0}
                        onChange={(value) => updateSelectedField((field) => ({
                          ...field,
                          style: { ...field.style, marginBottom: value || 0 }
                        }))}
                        addonAfter="px"
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  {selectedPath?.kind === 'tableCell' && selectedTable && (
                    <Row gutter={8}>
                      <Col span={12}>
                        <p className="option-label">Merge rows</p>
                        <InputNumber
                          min={1}
                          max={(selectedTable.tableRows || 1) - selectedPath.rowIndex}
                          value={selectedField.rowSpan || 1}
                          onChange={(value) => handleTableCellSpanChange('rowSpan', value)}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={12}>
                        <p className="option-label">Merge columns</p>
                        <InputNumber
                          min={1}
                          max={(selectedTable.tableColumns || 1) - selectedPath.columnIndex}
                          value={selectedField.colSpan || 1}
                          onChange={(value) => handleTableCellSpanChange('colSpan', value)}
                          style={{ width: '100%' }}
                        />
                      </Col>
                    </Row>
                  )}

                  {selectedField.type === 'container' && (
                    <>
                      {(selectedField.columnWidths || []).map((width, index) => (
                        <div key={index}>
                          <p className="option-label">Column {index + 1} width</p>
                          <Slider
                            min={10}
                            max={100}
                            value={width}
                            onChange={(value) => handleColumnWidthChange(index, value)}
                          />
                        </div>
                      ))}
                    </>
                  )}

                  {selectedField.type === 'table' && (
                    <>
                      <Checkbox
                        checked={selectedField.style?.tableBorder !== false}
                        onChange={(event) => updateSelectedField((field) => ({
                          ...field,
                          style: { ...field.style, tableBorder: event.target.checked }
                        }))}
                      >
                        Show table border
                      </Checkbox>

                      <Row gutter={8}>
                        <Col span={12}>
                          <p className="option-label">Rows</p>
                          <Space.Compact style={{ width: '100%' }}>
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              disabled={(selectedField.tableRows || 1) <= 1}
                              onClick={() => handleTableRowChange('delete')}
                            />
                            <InputNumber
                              min={1}
                              max={12}
                              value={selectedField.tableRows || 3}
                              onChange={(value) => handleTableSizeChange('tableRows', value || 1)}
                              style={{ width: '100%' }}
                            />
                            <Button
                              icon={<PlusOutlined />}
                              disabled={(selectedField.tableRows || 1) >= 12}
                              onClick={() => handleTableRowChange('add')}
                            />
                          </Space.Compact>
                        </Col>
                        <Col span={12}>
                          <p className="option-label">Columns</p>
                          <Space.Compact style={{ width: '100%' }}>
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              disabled={(selectedField.tableColumns || 1) <= 1}
                              onClick={() => handleTableColumnChange('delete')}
                            />
                            <InputNumber
                              min={1}
                              max={8}
                              value={selectedField.tableColumns || 2}
                              onChange={(value) => handleTableSizeChange('tableColumns', value || 1)}
                              style={{ width: '100%' }}
                            />
                            <Button
                              icon={<PlusOutlined />}
                              disabled={(selectedField.tableColumns || 1) >= 8}
                              onClick={() => handleTableColumnChange('add')}
                            />
                          </Space.Compact>
                        </Col>
                      </Row>
                      {(selectedField.tableColumnWidths || []).map((width, index) => (
                        <div key={index}>
                          <p className="option-label">Table column {index + 1} width</p>
                          <Slider
                            min={10}
                            max={100}
                            value={width}
                            onChange={(value) => handleTableColumnWidthChange(index, value)}
                          />
                        </div>
                      ))}
                    </>
                  )}

                  {(selectedField.type === 'image' || selectedField.fieldId === 'logo') && (
                    <>
                      <div>
                        <p className="option-label">Logo width</p>
                        <InputNumber
                          min={40}
                          max={300}
                          value={selectedField.style?.logoWidth || 120}
                          onChange={(value) => updateSelectedField((field) => ({
                            ...field,
                            style: { ...field.style, logoWidth: value || 120 }
                          }))}
                          addonAfter="px"
                          style={{ width: '100%' }}
                        />
                      </div>
                      
                      <div>
                        <p className="option-label">Logo height</p>
                        <InputNumber
                          min={24}
                          max={160}
                          value={selectedField.style?.logoHeight || 54}
                          onChange={(value) => updateSelectedField((field) => ({
                            ...field,
                            style: { ...field.style, logoHeight: value || 54 }
                          }))}
                          addonAfter="px"
                          style={{ width: '100%' }}
                        />
                      </div>
                    </>
                  )}

                  {selectedField.type === 'customText' && (
                    <div>
                      <p className="option-label">Custom Text Content</p>
                      <Input.TextArea
                        rows={4}
                        value={selectedField.customValue || selectedField.defaultValue || ''}
                        onChange={(event) => updateSelectedField((field) => ({
                          ...field,
                          customValue: event.target.value
                        }))}
                        placeholder="Enter your custom text, heading, or disclosure message"
                      />
                    </div>
                  )}
                </Space>
              ) : (
                <Empty
                  description="Select a field, header template, or table cell"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Card>

            <Card title="Section Layout" size="small" className="section-options">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Segmented
                  block
                  value={selectedSection}
                  onChange={setSelectedSection}
                  options={[
                    { label: 'Header', value: 'header' },
                    { label: 'Body', value: 'sections' },
                    { label: 'Footer', value: 'footer' }
                  ]}
                />

                <div>
                  <p className="option-label">Section width</p>
                  <Slider
                    min={40}
                    max={100}
                    value={templateLayout.sectionSettings?.[selectedSection]?.width || 100}
                    onChange={(value) => handleSectionSettingChange(selectedSection, 'width', value)}
                  />
                </div>

                <div>
                  <p className="option-label">Section position</p>
                  <Segmented
                    block
                    value={templateLayout.sectionSettings?.[selectedSection]?.align || 'left'}
                    onChange={(value) => handleSectionSettingChange(selectedSection, 'align', value)}
                    options={[
                      { label: 'Left', value: 'left' },
                      { label: 'Center', value: 'center' },
                      { label: 'Right', value: 'right' }
                    ]}
                  />
                </div>

                <Row gutter={8}>
                  <Col span={12}>
                    <p className="option-label">Top margin</p>
                    <InputNumber
                      min={0}
                      max={80}
                      value={templateLayout.sectionSettings?.[selectedSection]?.marginTop || 0}
                      onChange={(value) => handleSectionSettingChange(selectedSection, 'marginTop', value || 0)}
                      addonAfter="px"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={12}>
                    <p className="option-label">Bottom margin</p>
                    <InputNumber
                      min={0}
                      max={80}
                      value={templateLayout.sectionSettings?.[selectedSection]?.marginBottom || 0}
                      onChange={(value) => handleSectionSettingChange(selectedSection, 'marginBottom', value || 0)}
                      addonAfter="px"
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>

                <Row gutter={8}>
                  <Col span={12}>
                    <p className="option-label">Left margin</p>
                    <InputNumber
                      min={0}
                      max={120}
                      value={templateLayout.sectionSettings?.[selectedSection]?.marginLeft || 0}
                      onChange={(value) => handleSectionSettingChange(selectedSection, 'marginLeft', value || 0)}
                      addonAfter="px"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={12}>
                    <p className="option-label">Right margin</p>
                    <InputNumber
                      min={0}
                      max={120}
                      value={templateLayout.sectionSettings?.[selectedSection]?.marginRight || 0}
                      onChange={(value) => handleSectionSettingChange(selectedSection, 'marginRight', value || 0)}
                      addonAfter="px"
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>

        <Card
          title="Preview"
          size="small"
          className="bottom-preview"
          extra={<EyeOutlined />}
        >
          {renderTemplatePreview()}
        </Card>
          </>
        )}
      </Form>
    </Modal>
  );
}

export default PayslipTemplateBuilder;
